import { useState } from 'react';
import { useInventory, useUpdateInventory, useBatchUpdateInventory } from '../hooks/useInventory';
import { StickerCard, teamColor } from '../components/sticker/StickerCard';
import { StickerFilters } from '../components/sticker/StickerFilters';
import type { ViewMode } from '../components/sticker/StickerFilters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import type { StickerStatus, InventoryItem } from '../types';

// ── Group mapping ─────────────────────────────────────────────────────────
const TEAM_TO_GROUP: Record<string, string> = {
  // Group A
  'Mexico': 'A', 'South Africa': 'A', 'Korea Republic': 'A', 'Czechia': 'A',
  // Group B
  'Canada': 'B', 'Bosnia-Herzegovina': 'B', 'Qatar': 'B', 'Switzerland': 'B',
  // Group C
  'Brazil': 'C', 'Morocco': 'C', 'Haiti': 'C', 'Scotland': 'C',
  // Group D
  'United States': 'D', 'Paraguay': 'D', 'Australia': 'D', 'Türkiye': 'D',
  // Groups E–L (pending catalog)
  'Argentina': 'E', 'Colombia': 'E', 'New Zealand': 'E', 'Algeria': 'E',
  'France': 'F', 'Belgium': 'F', 'Senegal': 'F', 'Uzbekistan': 'F',
  'Portugal': 'G', 'Croatia': 'G', 'Ecuador': 'G', 'Saudi Arabia': 'G',
  'Spain': 'H', 'Germany': 'H', 'Japan': 'H', 'Curaçao': 'H',
  'England': 'I', 'Netherlands': 'I', 'Iran': 'I', 'Panama': 'I',
  'Uruguay': 'J', 'Austria': 'J', 'Egypt': 'J', 'Jordan': 'J',
  'Norway': 'K', "Côte d'Ivoire": 'K', 'Tunisia': 'K', 'Cape Verde': 'K',
  'Cameroon': 'L', 'Honduras': 'L',
};

function getGroup(nationalTeam: string): string {
  if (nationalTeam === 'FWC') return 'FWC';
  return TEAM_TO_GROUP[nationalTeam] ?? '?';
}

// Pure helper used by Property 8 in stickers.property.test.ts
export function groupByTeam(items: InventoryItem[]): Record<string, InventoryItem[]> {
  return items.reduce<Record<string, InventoryItem[]>>((acc, item) => {
    const t = item.nationalTeam;
    if (!acc[t]) acc[t] = [];
    acc[t].push(item);
    return acc;
  }, {});
}

// Pure helper used by Property 8 in stickers.property.test.ts
function parseNumbers(input: string): number[] {
  return [...new Set(
    input.split(/[\s,;]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n >= 1)
  )];
}

// ── Carga rápida ────────────────────────────────────────────────────────────
function CargaRapida({
  inventory,
  onClose,
}: {
  inventory: InventoryItem[];
  onClose: () => void;
}) {
  const batchUpdate = useBatchUpdateInventory();
  const [tengoInput, setTengoInput]       = useState('');
  const [repetidaInput, setRepetidaInput] = useState('');
  const [progress, setProgress]           = useState<{ done: number; total: number } | null>(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');

  // Map albumNumber → stickerId  (e.g. 19 → "MEX-9")
  const albumNumberToStickerId = new Map<number, string>(
    inventory.map((i) => [i.albumNumber, i.stickerId])
  );

  const tengoNums    = parseNumbers(tengoInput);
  const repetidaNums = parseNumbers(repetidaInput);

  // Build updateMap: stickerId → quantity
  const updateMap = new Map<string, number>();
  for (const n of tengoNums) {
    const sid = albumNumberToStickerId.get(n);
    if (sid) updateMap.set(sid, 1);
  }
  for (const n of repetidaNums) {
    const sid = albumNumberToStickerId.get(n);
    if (sid) updateMap.set(sid, Math.max(updateMap.get(sid) ?? 0, 2));
  }

  // Warn about numbers not found in catalog
  const inventoryLoaded = inventory.length > 0;
  const unknownTengo    = inventoryLoaded ? tengoNums.filter((n) => !albumNumberToStickerId.has(n)) : [];
  const unknownRepetida = inventoryLoaded ? repetidaNums.filter((n) => !albumNumberToStickerId.has(n)) : [];

  async function handleSubmit() {
    if (updateMap.size === 0) return;
    setLoading(true);
    setError('');
    try {
      const updates = [...updateMap.entries()].map(([stickerId, quantity]) => ({
        stickerId,
        quantity,
      }));
      await batchUpdate(updates, (done, total) => setProgress({ done, total }));
      onClose();
    } catch {
      setError('Error al cargar. Intenta de nuevo.');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }

  return (
    <div className="bg-white rounded-2xl p-5 shadow-card border border-brand-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">Carga rápida</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-lg leading-none">✕</button>
      </div>

      <p className="text-xs text-gray-400 font-semibold mb-4">
        Escribe los números separados por espacio, coma o Enter.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1.5">
            Tengo estas figuritas
          </label>
          <textarea
            value={tengoInput}
            onChange={(e) => setTengoInput(e.target.value)}
            placeholder="1 5 23 44 67 102..."
            rows={4}
            className="input-field resize-none font-mono text-sm"
          />
          {unknownTengo.length > 0 && (
            <p className="text-[11px] text-amber-600 font-semibold mt-1">
              Números no encontrados: {unknownTengo.join(', ')}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-amber-500 uppercase tracking-wide mb-1.5">
            Tengo repetidas de estas
          </label>
          <textarea
            value={repetidaInput}
            onChange={(e) => setRepetidaInput(e.target.value)}
            placeholder="12 34 78..."
            rows={4}
            className="input-field resize-none font-mono text-sm"
          />
          {unknownRepetida.length > 0 && (
            <p className="text-[11px] text-amber-600 font-semibold mt-1">
              Números no encontrados: {unknownRepetida.join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Preview */}
      {updateMap.size > 0 && (
        <div className="mt-3 flex gap-3 text-xs font-semibold flex-wrap">
          <span className="text-emerald-600">
            {[...updateMap.values()].filter((q) => q === 1).length} figuritas → TENGO
          </span>
          <span className="text-amber-600">
            {[...updateMap.values()].filter((q) => q >= 2).length} figuritas → DUPLICADA
          </span>
          <span className="text-gray-400 ml-auto">
            {updateMap.size} actualizaciones totales
          </span>
        </div>
      )}

      {progress && (
        <div className="mt-3">
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 bg-brand-500 rounded-full transition-all duration-300"
              style={{ width: `${(progress.done / progress.total) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 font-semibold mt-1 text-right">
            {progress.done} / {progress.total}
          </p>
        </div>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600 font-semibold">{error}</p>
      )}

      <div className="flex gap-2 mt-4">
        <Button
          onClick={handleSubmit}
          loading={loading}
          disabled={updateMap.size === 0}
        >
          Cargar {updateMap.size > 0 ? `${updateMap.size} figuritas` : ''}
        </Button>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}

// ── Checklist view ───────────────────────────────────────────────────────────
function ChecklistView({
  items,
  onTap,
}: {
  items: InventoryItem[];
  onTap: (stickerId: string, currentQty: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1 text-[11px] text-gray-400 font-semibold mb-3">
      <div className="flex gap-3 flex-wrap">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-200 inline-block" /> Sin figurita
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-emerald-400 inline-block" /> Tengo
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-amber-400 inline-block" /> Repetida
        </span>
        <span className="ml-auto">Toca para cambiar estado</span>
      </div>

      <div
        className="grid gap-1 mt-2"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))' }}
      >
        {items.map((item) => {
          const isTengo = item.status === 'TENGO';
          const isDup   = item.status === 'DUPLICADA';
          return (
            <button
              key={item.stickerId}
              onClick={() => onTap(item.stickerId, item.quantity)}
              className={[
                'h-12 rounded-xl flex flex-col items-center justify-center gap-0 font-bold transition-all active:scale-95',
                isTengo ? 'bg-emerald-400 text-white shadow-sm'
                  : isDup ? 'bg-amber-400 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
              ].join(' ')}
            >
              <span className="text-sm leading-none">{item.stickerId}</span>
              {isDup && (
                <span className="text-[10px] leading-none opacity-90">+{item.quantity - 1}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function InventoryPage() {
  const [statusFilter, setStatusFilter] = useState<StickerStatus | 'ALL'>('ALL');
  const [view, setView]                 = useState<ViewMode>('grid');
  const [showCarga, setShowCarga]       = useState(false);
  const { data: inventory, isLoading }  = useInventory();
  const updateInventory                 = useUpdateInventory();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  const items    = inventory ?? [];
  const sorted   = [...items].sort((a, b) => (a.albumNumber ?? 0) - (b.albumNumber ?? 0));
  const filtered = statusFilter === 'ALL'
    ? sorted
    : statusFilter === 'TENGO'
      ? sorted.filter((i) => i.status === 'TENGO' || i.status === 'DUPLICADA')
      : sorted.filter((i) => i.status === statusFilter);
  const total      = sorted.length;
  const tengo      = sorted.filter((i) => i.status === 'TENGO').length;
  const falta      = sorted.filter((i) => i.status === 'FALTA').length;
  // duplicadas = stickers DUPLICADA (figuritas únicas con al menos 1 extra)
  const duplicadas = sorted.filter((i) => i.status === 'DUPLICADA').length;
  // repetidas = total copias extras = suma de (quantity-1) por cada DUPLICADA
  const repetidas  = sorted
    .filter((i) => i.status === 'DUPLICADA')
    .reduce((sum, i) => sum + (i.quantity - 1), 0);
  // figuritas únicas que tengo = TENGO + DUPLICADA (cada una cuenta 1 en el álbum)
  const tengoTotal = tengo + duplicadas;
  const pct        = total > 0 ? Math.round((tengoTotal / total) * 100) : 0;

  function handleUpdate(stickerId: string, quantity: number) {
    updateInventory.mutate({ stickerId, quantity });
  }

  // Checklist tap: cycle FALTA→TENGO→DUPLICADA→FALTA
  function handleChecklistTap(stickerId: string, currentQty: number) {
    const next = currentQty === 0 ? 1 : currentQty === 1 ? 2 : 0;
    handleUpdate(stickerId, next);
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Stats header */}
      <div className="bg-white rounded-2xl p-5 shadow-card border border-amber-100">
        <div className="flex items-baseline justify-between mb-3">
          <h1 className="text-2xl font-display text-night" style={{ fontFamily: '"Fredoka One", cursive' }}>
            Mi Álbum
          </h1>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-400">{tengoTotal} / {total}</span>
            <button
              onClick={() => { setShowCarga((v) => !v); }}
              className={[
                'px-3 py-1.5 rounded-lg text-xs font-bold border transition-all',
                showCarga
                  ? 'bg-brand-500 text-white border-brand-500'
                  : 'bg-white text-brand-500 border-brand-200 hover:border-brand-400',
              ].join(' ')}
            >
              ⚡ Carga rápida
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-100 rounded-full h-3 mb-4 overflow-hidden">
          <div
            className="h-3 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>

        {/* Stat chips */}
        <div className="flex gap-3 flex-wrap">
          <StatChip value={tengo}      label="Tengo"      chipClass="bg-green-50 text-green-700"  dotClass="bg-green-500" />
          <StatChip value={falta}      label="Faltan"     chipClass="bg-red-50 text-red-600"      dotClass="bg-red-500" />
          <StatChip value={repetidas}  label="Repetidas"  chipClass="bg-amber-50 text-amber-700"  dotClass="bg-amber-400" />
          <span className="ml-auto text-xs text-gray-400 font-bold self-center">{pct}% completado</span>
        </div>
      </div>

      {/* Carga rápida panel */}
      {showCarga && (
        <CargaRapida inventory={sorted} onClose={() => setShowCarga(false)} />
      )}

      {/* Filters */}
      <StickerFilters
        status={statusFilter}
        onStatusChange={setStatusFilter}
        view={view}
        onViewChange={setView}
      />

      {/* Checklist legend + grid */}
      {view === 'check' && (
        filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <ChecklistView items={filtered} onTap={handleChecklistTap} />
        )
      )}

      {/* Empty state (non-checklist) */}
      {view !== 'check' && filtered.length === 0 && <EmptyState />}

      {/* Grid view */}
      {view === 'grid' && filtered.length > 0 && (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
          {filtered.map((item) => (
            <StickerCard key={item.stickerId} item={item} onUpdateQuantity={handleUpdate} allowPhoto />
          ))}
        </div>
      )}

      {/* List view */}
      {view === 'list' && filtered.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {filtered.map((item) => (
            <div
              key={item.stickerId}
              className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3 shadow-sm hover:shadow-card transition-shadow"
            >
              <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: teamColor(item.nationalTeam) }} />
              <span className="text-xs font-bold text-gray-400 w-8 flex-shrink-0">#{item.albumNumber ?? item.stickerId}</span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate text-gray-800">{item.playerName}</div>
                <div className="text-xs text-gray-400 truncate">{item.nationalTeam}</div>
              </div>
              <StatusBadge status={item.status} />
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleUpdate(item.stickerId, Math.max(0, item.quantity - 1))}
                  className="w-6 h-6 rounded-md bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-500 font-bold flex items-center justify-center text-sm transition-colors"
                >−</button>
                <span className="w-5 text-center text-sm font-bold text-gray-700">{item.quantity}</span>
                <button
                  onClick={() => handleUpdate(item.stickerId, item.quantity + 1)}
                  className="w-6 h-6 rounded-md bg-gray-100 hover:bg-green-100 hover:text-green-600 text-gray-500 font-bold flex items-center justify-center text-sm transition-colors"
                >+</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Team view */}
      {view === 'team' && filtered.length > 0 && (
        <TeamView items={filtered} onUpdate={handleUpdate} />
      )}

      {/* Group view */}
      {view === 'group' && filtered.length > 0 && (
        <GroupView items={filtered} onUpdate={handleUpdate} />
      )}
      {view === 'group' && filtered.length === 0 && <EmptyState />}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 text-gray-400">
      <div className="text-5xl mb-3">📭</div>
      <p className="font-semibold">No hay figuritas con este filtro</p>
    </div>
  );
}

// ── Group view ───────────────────────────────────────────────────────────────
function GroupView({ items, onUpdate }: { items: InventoryItem[]; onUpdate: (id: string, qty: number) => void }) {
  // Group items by group letter (FWC, A, B, C...)
  const grouped = items.reduce<Record<string, InventoryItem[]>>((acc, item) => {
    const g = getGroup(item.nationalTeam);
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const groupOrder = ['FWC', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

  return (
    <div className="flex flex-col gap-6">
      {groupOrder
        .filter((g) => grouped[g]?.length)
        .map((groupLabel) => {
          const groupItems = grouped[groupLabel];
          const tengo  = groupItems.filter((i) => i.status === 'TENGO' || i.status === 'DUPLICADA').length;
          const total  = groupItems.length;
          const pct    = total > 0 ? Math.round((tengo / total) * 100) : 0;
          const title  = groupLabel === 'FWC' ? 'Generales FWC' : `Grupo ${groupLabel}`;

          return (
            <div key={groupLabel}>
              {/* Group header */}
              <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-100">
                <div className="w-8 h-8 rounded-lg bg-night text-white flex items-center justify-center font-display font-bold text-sm flex-shrink-0"
                  style={{ fontFamily: '"Fredoka One", cursive' }}>
                  {groupLabel}
                </div>
                <h3 className="font-display text-lg text-night" style={{ fontFamily: '"Fredoka One", cursive' }}>
                  {title}
                </h3>
                <span className="text-xs text-gray-400 font-semibold">({total})</span>
                <div className="ml-auto flex items-center gap-3">
                  <div className="w-20 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-gray-400">{tengo}/{total}</span>
                </div>
              </div>
              {/* Sticker grid */}
              <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                {groupItems.map((item) => (
                  <StickerCard key={item.stickerId} item={item} onUpdateQuantity={onUpdate} />
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
}

// ── Team view ───────────────────────────────────────────────────────────────
function TeamView({ items, onUpdate }: { items: InventoryItem[]; onUpdate: (id: string, qty: number) => void }) {
  const grouped = groupByTeam(items);
  return (
    <div className="flex flex-col gap-6">
      {Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([team, teamItems]) => {
          const color      = teamColor(team);
          const teamTengo  = teamItems.filter((i) => i.status === 'TENGO').length;
          const teamFalta  = teamItems.filter((i) => i.status === 'FALTA').length;
          return (
            <div key={team}>
              <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-100">
                <div className="w-1 h-7 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <h3 className="font-display text-lg text-night" style={{ fontFamily: '"Fredoka One", cursive' }}>
                  {team}
                </h3>
                <span className="text-xs text-gray-400 font-semibold">({teamItems.length})</span>
                <div className="ml-auto flex gap-3 text-xs font-bold">
                  <span className="text-green-600">{teamTengo} tengo</span>
                  <span className="text-red-500">{teamFalta} faltan</span>
                </div>
              </div>
              <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                {teamItems.map((item) => (
                  <StickerCard key={item.stickerId} item={item} onUpdateQuantity={onUpdate} />
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
}

function StatChip({ value, label, chipClass, dotClass }: {
  value: number; label: string; chipClass: string; dotClass: string;
}) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${chipClass}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
      <span className="text-base font-extrabold">{value}</span>
      <span className="opacity-80">{label}</span>
    </div>
  );
}
